import React, { memo, useCallback } from 'react'
import { ControlledCheckbox } from 'transfix-form/components'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { TCarrierFleetInformationSubmitHandler } from '../../types'
import {
  IUseFreightCapabilityProps,
  useFreightCapabilityForm,
} from './hooks/useFreightCapabilityForm'
import { TFreightCapabilitySchema } from './schema/FreightCapabilitySchema'

export interface IFreightCapabilityModalProps extends Pick<IFormModalProps, 'onClose'> {
  /**
   * @default {}
   */
  defaultValues?: IUseFreightCapabilityProps['defaultValues']
  loading: boolean
  onSubmit: TCarrierFleetInformationSubmitHandler<TFreightCapabilitySchema>
}

const FreightCapabilityModal = memo<IFreightCapabilityModalProps>(
  ({ defaultValues = {}, loading, onClose, onSubmit }) => {
    const { control, handleSubmit } = useFreightCapabilityForm({ defaultValues })

    const submitHandler = useCallback(
      async (formValues: TFreightCapabilitySchema) => {
        const success = await onSubmit(formValues)
        if (success) {
          onClose()
        }
      },
      [onClose, onSubmit]
    )

    const actions: IFormModalProps['actions'] = [
      {
        label: 'Cancel',
        type: 'dismiss',
        action: onClose,
        disabled: loading,
      },
      {
        label: 'Update',
        type: 'confirm',
        action: handleSubmit(submitHandler),
        disabled: loading,
      },
    ]

    return (
      <FormModal title='Freight capabilities' actions={actions} size={5} onClose={onClose} open>
        <Grid container>
          <Grid item xs={12}>
            <Box mb={1.5}>
              <Typography component='div'>Select your freight capabilities</Typography>
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='isFtlCapable'
              CheckboxProps={{ label: 'FTL' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='isLtlCapable'
              CheckboxProps={{ label: 'LTL' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='isIntermodalCapable'
              CheckboxProps={{ label: 'Intermodal' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='isDrayageCapable'
              CheckboxProps={{ label: 'Drayage' }}
            />
          </Grid>
        </Grid>
      </FormModal>
    )
  }
)

export default FreightCapabilityModal
